import {
  ArrowPathIcon,
  HandRaisedIcon,
  PaperAirplaneIcon,
  PlayIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { useQueue, useQueueAction, useQueueFlush } from "@/api/hooks";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { QueueEntry } from "@/api/client";
import { formatBytes, relTime } from "@/lib/utils";

export default function QueuePage() {
  const { data, isLoading, isFetching, refetch } = useQueue();
  const flush = useQueueFlush();
  const del = useQueueAction("delete");
  const hold = useQueueAction("hold");
  const release = useQueueAction("release");

  const entries = data?.entries ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="text-heading text-3xl font-light sm:text-4xl">
            <span className="text-bitrefinery-red">Q</span>ueue
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-bitrefinery-text text-body">
            Postfix mail queue. Refreshes every 15 seconds. Messages sit here when delivery
            is deferred (remote server down, greylisting, DNS miss). Use <em>flush</em> to
            retry everything now.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={isFetching}
            title="Refetch now"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Refresh
          </Button>
          <Button
            onClick={() => flush.mutateAsync()}
            disabled={flush.isPending || entries.length === 0}
          >
            <PaperAirplaneIcon className="h-4 w-4" />
            Flush all
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Queue ID</TableHead>
              <TableHead>Age</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Sender</TableHead>
              <TableHead>Recipients / status</TableHead>
              <TableHead className="w-[1%] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && entries.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                  Queue is empty.
                </TableCell>
              </TableRow>
            )}
            {entries.map((e) => (
              <TableRow key={e.queue_id}>
                <TableCell className="font-mono text-xs">
                  {e.queue_id}
                  {e.queue_name && (
                    <Badge variant="outline" className="ml-2 text-[10px]">
                      {e.queue_name}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {relTime(e.arrival_time)}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatSize(e.message_size)}
                </TableCell>
                <TableCell className="font-mono text-xs">{e.sender ?? "—"}</TableCell>
                <TableCell className="space-y-0.5">
                  {(e.recipients ?? []).map((r, i) => (
                    <div key={i} className="text-xs">
                      <span className="font-mono">{r.address}</span>
                      {r.delay_reason && (
                        <span className="ml-2 text-muted-foreground">{r.delay_reason}</span>
                      )}
                    </div>
                  ))}
                </TableCell>
                <TableCell className="flex justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Flush (retry now)"
                    onClick={() => release.mutate(e.queue_id)}
                  >
                    <PlayIcon className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Hold (pause retries)"
                    onClick={() => hold.mutate(e.queue_id)}
                  >
                    <HandRaisedIcon className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Delete"
                    onClick={() => {
                      if (confirm(`Delete ${e.queue_id}?`)) del.mutate(e.queue_id);
                    }}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function formatSize(n: number | undefined): string {
  if (!n) return "—";
  return formatBytes(n);
}
